<template>
	<b-overlay :show="!!(userData.id && !userData.is_subscribed)" variant="dark" opacity="0.75">
		<template #overlay>
			<div class="text-center p-4">
				<p class="mb-3">The gift goal widget is available for subscribed users only.</p>
				<b-button variant="warning" @click="$root.$emit('show-upgrade-modal')">Upgrade</b-button>
			</div>
		</template>
	<widget-settings-layout ref="layout" v-if="settings" slug="giftGoal" name="Gift Goal" :error="saveError" :saving="saving" :has-unsaved-changes="hasUnsavedChanges" :settings="settings" :saved-settings-json="savedSettingsJson" @save="save">
		<b-card class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-checkbox inline v-model="settings.hidePercentage">Hide percentage</b-form-checkbox>
				</b-col>
				<b-col>
					<b-form-checkbox inline v-model="settings.hideCurrency">Hide currency</b-form-checkbox>
				</b-col>
				<b-col>
					<b-form-checkbox inline v-model="settings.centerGoal">Center goal amount</b-form-checkbox>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Goal">
						<b-input-group>
							<b-form-input v-model="settings.goal"></b-form-input>
							<b-input-group-append>
								<b-input-group-text>{{userData.currency_code}}</b-input-group-text>
							</b-input-group-append>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Amount">
						<b-input-group>
							<b-form-input v-model="settings.amount"></b-form-input>
							<b-input-group-append>
								<b-input-group-text>{{userData.currency_code}}</b-input-group-text>
							</b-input-group-append>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col cols="8">
					<b-form-group label="Title">
						<b-input-group>
							<b-form-input v-model="settings.title"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Append to goal">
						<b-input-group>
							<b-form-input v-model="settings.appendToGoal"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card class="mb-3">
			<b-form-row>
				<b-col cols="6">
					<div class="form-group">
						<div class="float-right"><b-form-checkbox size="sm" v-model="settings.customFont">Custom</b-form-checkbox></div>
						<label for="fontFamilyInput">Font family</label>
						<VueTypeaheadBootstrap
							v-model="settings.fontFamily"
							:data="settings.customFont ? [] : googleFonts"
							:serializer="item => `${item.family}`"
							placeholder="Search fonts..."
							disable-sort
							autocomplete="off"
							required
							id="fontFamilyInput"
						/>
					</div>
				</b-col>
				<b-col>
					<b-form-group label="Font size" append="px">
						<b-input-group append="px">
							<b-form-input type="number" min="0" max="999" step="any" v-model="settings.fontSize"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Letter spacing">
						<b-input-group append="px">
							<b-form-input type="number" min="0" max="999" step="any" v-model="settings.letterSpacing"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Formatting">
						<b-button-group class="d-flex w-100">
							<b-button v-b-tooltip.hover.bottom title="Bold" :pressed="settings.boldText" @click="settings.boldText = !settings.boldText" variant="outline-secondary">
								<b-icon icon="type-bold" aria-hidden="true"></b-icon>
							</b-button>
							<b-button v-b-tooltip.hover.bottom title="Italic" :pressed="settings.italicText" @click="settings.italicText = !settings.italicText" variant="outline-secondary">
								<b-icon icon="type-italic" aria-hidden="true"></b-icon>
							</b-button>
							<b-button v-b-tooltip.hover.bottom title="Underline" :pressed="settings.underlineText" @click="settings.underlineText = !settings.underlineText" variant="outline-secondary">
								<b-icon icon="type-underline" aria-hidden="true"></b-icon>
							</b-button>
							<b-button v-b-tooltip.hover.bottom title="Strikethrough" :pressed="settings.strikethroughText" @click="settings.strikethroughText = !settings.strikethroughText" variant="outline-secondary">
								<b-icon icon="type-strikethrough" aria-hidden="true"></b-icon>
							</b-button>
							<b-button v-b-tooltip.hover.bottom title="Uppercase" :pressed="settings.uppercaseText" @click="settings.uppercaseText = !settings.uppercaseText" variant="outline-secondary">
								<b-icon icon="align-top" aria-hidden="true"></b-icon>
							</b-button>
						</b-button-group>
					</b-form-group>
				</b-col>
				<b-col cols="5">
				<b-form-group label="Vertical align">
					<b-button-group class="d-flex w-100">
						<b-button v-b-tooltip.hover.bottom title="Top"    :pressed="settings.verticalAlign == 'top'"    @click="settings.verticalAlign = 'top'"    variant="outline-secondary"><b-icon icon="align-top"    aria-hidden="true"></b-icon></b-button>
						<b-button v-b-tooltip.hover.bottom title="Middle" :pressed="settings.verticalAlign == 'middle'" @click="settings.verticalAlign = 'middle'" variant="outline-secondary"><b-icon icon="align-middle" aria-hidden="true"></b-icon></b-button>
						<b-button v-b-tooltip.hover.bottom title="Bottom" :pressed="settings.verticalAlign == 'bottom'" @click="settings.verticalAlign = 'bottom'" variant="outline-secondary"><b-icon icon="align-bottom" aria-hidden="true"></b-icon></b-button>
					</b-button-group>
				</b-form-group>
			</b-col>
			</b-form-row>
		</b-card>
		<b-card class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Text Color">
						<color-picker v-model="settings.textColor" />
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Bar color">
						<color-picker v-model="settings.barColor" />
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Back color">
						<color-picker v-model="settings.backColor" />
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Padding" append="px">
						<b-input-group append="px">
							<b-input type="number" min="0" max="2000" v-model="settings.padding"/>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Border width">
						<b-input-group append="px">
							<b-form-input type="number" min="0" max="100" step="any" v-model="settings.borderWidth"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Border radius" append="px">
						<b-input-group append="px">
							<b-input type="number" min="0" max="2000" v-model="settings.borderRadius"/>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col cols="6">
					<b-form-group label="Border color">
						<color-picker v-model="settings.borderColor" />
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Text outline blur">
						<b-input-group append="px">
							<b-form-input type="number" min="0" max="100" step="any" v-model="settings.blurRadius"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Text outline size">
						<b-input-group append="px">
							<b-form-input type="number" min="0" max="10" step="any" v-model="settings.spreadRadius"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col cols="6">
					<b-form-group label="Text Outline Color">
						<color-picker v-model="settings.shadowColor" />
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card class="mb-3">
			<b-form-group label="Extra CSS">
				<b-textarea v-model="settings.extraCSS"></b-textarea>
			</b-form-group>
		</b-card>
		<template #preview>
			<gift-goal-widget-preview :settings="settings || {}" />
		</template>
	</widget-settings-layout>
	</b-overlay>
</template>

<script>
import VueTypeaheadBootstrap from 'vue-typeahead-bootstrap';
import animateCssTransitions from '../../animateCssTransitions';
import ColorPicker from '@/components/ColorPicker.vue';
import WidgetSettingsLayout from '@/components/Widget/WidgetSettingsLayout.vue';
import GiftGoalWidgetPreview from '@/components/Widget/GiftGoalWidgetPreview.vue';

export default {
	beforeRouteLeave(to, from, next) {
		if (!this.hasUnsavedChanges) return next();
		this.$refs.layout.showLeaveModal(next, () => this.save());
	},
	data() { return {
		loading: 0,
		error: null,
		saving: 0,
		saveError: null,
		settings: null,
		userData: { currency_code: '' },
		inTransitions: [ ...[{ text: 'Don\'t fade in', value: null }], ...animateCssTransitions.inTransitions ],
		outTransitions: [ ...[{ text: 'Don\'t fade out (log mode)', value: null }], ...animateCssTransitions.outTransitions ],
		curColor: {},
		savedSettingsJson: null,
		googleFonts: [],
	}; },
	components: {
		VueTypeaheadBootstrap,
		ColorPicker,
		WidgetSettingsLayout,
		GiftGoalWidgetPreview,
	},
	async mounted() {
		window.addEventListener('beforeunload', this._onBeforeUnload);
		try {
			this.loading++;
			this.error = null;
			this.userData = await this.$api.get('userData');
			const settings = await this.$api.get('widgetSettings/gift-goal');
			this.settings = {
				...settings,
				enableTTS: settings.enableTTS||false,
				enableEventSounds: settings.enableEventSounds||false,
				inTransition: settings.inTransition||null,
				outTransition: settings.outTransition||null,
				textColor: settings.textColor || 'rgba(255, 255, 255, 1)',
				backColor: settings.backColor || 'rgba(0, 0, 0, 1)',
				barColor: settings.barColor || 'rgba(0, 127, 255, 1)',
				shadowColor: settings.shadowColor || 'rgba(0, 0, 0, 1)',
				borderColor: settings.borderColor || 'rgba(0, 127, 255, 1)',
				borderWidth: settings.borderWidth || 0,
				fontFamily: settings.fontFamily || '',
				fontSize: settings.fontSize || 0,
				blurRadius: settings.blurRadius || 0,
				lineHeight: settings.lineHeight || 0,
				letterSpacing: settings.letterSpacing || 0,
				textAlign: settings.textAlign || 'left',
				verticalAlign: settings.verticalAlign || 'middle',
				boldText: settings.boldText || false,
				italicText: settings.italicText || false,
				underlineText: settings.underlineText || false,
				strikethroughText: settings.strikethroughText || false,
				uppercaseText: settings.uppercaseText || false,
				extraCSS: settings.extraCSS || '',
				customFont: settings.customFont || false,
			};
			this.savedSettingsJson = JSON.stringify(this.settings);
			this.subId = this.$socket.subscribe(this.userData?.widget_hash, {
				widgetSettingsFieldChange: msg => {
					if(msg.widgetType != 'gift-goal') return;
					if(msg.field == 'amount') this.settings.amount = msg.value;
				},
			});
			this.googleFonts = await this.$api.get('googleFonts');
		} catch(e) {
			this.error = e;
		} finally {
			this.loading--;
		}
	},
	computed: {
		hasUnsavedChanges() {
			if (this.savedSettingsJson === null) return false;
			return JSON.stringify(this.settings) !== this.savedSettingsJson;
		},
	},
	beforeDestroy() {
		window.removeEventListener('beforeunload', this._onBeforeUnload);
		this.subId && this.$socket.unsubscribe(this.subId);
	},
	methods: {
		_onBeforeUnload(e) {
			if (this.hasUnsavedChanges) {
				e.preventDefault();
				e.returnValue = '';
			}
		},
		async save() {
			try {
				this.saving++;
				this.saveError = null;

				await this.$api.post('widgetSettings/gift-goal', this.settings);
				this.savedSettingsJson = JSON.stringify(this.settings);
			} catch(e) {
				console.error('Error saving widget:', e);
				this.saveError = e;
			} finally {
				this.saving--;
			}
		},
	},
};
</script>
